'use client';

import { useCallback, useState } from 'react';
import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxWidthOrHeight?: number;
  maxSizeMB?: number;
  quality?: number;
  useWebWorker?: boolean;
  preserveExif?: boolean;
  fileType?: 'image/jpeg' | 'image/webp' | 'image/png';
}

export interface CompressionResult {
  originalFile: File;
  compressedFile: File;
  originalSize: number;
  compressedSize: number;
  savings: number;
  savingsPercent: number;
}

export interface UseImageCompressionResult {
  compress: (
    file: File,
    options?: CompressionOptions,
  ) => Promise<CompressionResult>;
  compressBatch: (
    files: File[],
    options?: CompressionOptions,
  ) => Promise<CompressionResult[]>;
  isCompressing: boolean;
  progress: number;
  error: Error | null;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidthOrHeight: 2048,
  maxSizeMB: 2,
  quality: 0.8,
  useWebWorker: true,
  preserveExif: false,
};

/**
 * Hook for client-side image compression
 *
 * Uses browser-image-compression for efficient in-browser compression.
 * Supports batch processing with progress tracking.
 *
 * @returns Compression functions and status
 *
 * @example
 * ```tsx
 * function ImageUpload() {
 *   const { compress, isCompressing, progress } = useImageCompression();
 *
 *   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
 *     const file = e.target.files?.[0];
 *     if (!file) return;
 *
 *     const result = await compress(file, {
 *       maxWidthOrHeight: 1920,
 *       maxSizeMB: 1,
 *       quality: 0.85,
 *     });
 *
 *     console.log(`Compressed from ${result.originalSize} to ${result.compressedSize}`);
 *     console.log(`Saved ${result.savingsPercent.toFixed(1)}%`);
 *   };
 *
 *   return (
 *     <div>
 *       <input type="file" accept="image/*" onChange={handleFileChange} />
 *       {isCompressing && <progress value={progress} max={100} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useImageCompression(): UseImageCompressionResult {
  const [isCompressing, setIsCompressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const compress = useCallback(
    async (
      file: File,
      options: CompressionOptions = {},
    ): Promise<CompressionResult> => {
      const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

      setIsCompressing(true);
      setProgress(0);
      setError(null);

      try {
        // Validate file is an image
        if (!file.type.startsWith('image/')) {
          throw new Error('File is not an image');
        }

        const originalSize = file.size;

        const compressedFile = await imageCompression(file, {
          maxWidthOrHeight: mergedOptions.maxWidthOrHeight,
          maxSizeMB: mergedOptions.maxSizeMB,
          useWebWorker: mergedOptions.useWebWorker,
          preserveExif: mergedOptions.preserveExif,
          fileType: mergedOptions.fileType,
          initialQuality: mergedOptions.quality,
          onProgress: (p) => setProgress(Math.round(p)),
        });

        const compressedSize = compressedFile.size;
        const savings = originalSize - compressedSize;
        const savingsPercent =
          originalSize > 0 ? (savings / originalSize) * 100 : 0;

        setProgress(100);

        return {
          originalFile: file,
          compressedFile,
          originalSize,
          compressedSize,
          savings,
          savingsPercent,
        };
      } catch (err) {
        const compressionError =
          err instanceof Error ? err : new Error('Compression failed');
        setError(compressionError);
        throw compressionError;
      } finally {
        setIsCompressing(false);
      }
    },
    [],
  );

  const compressBatch = useCallback(
    async (
      files: File[],
      options: CompressionOptions = {},
    ): Promise<CompressionResult[]> => {
      setIsCompressing(true);
      setProgress(0);
      setError(null);

      const results: CompressionResult[] = [];
      const total = files.length;

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (!file) continue;

          // Skip non-image files
          if (!file.type.startsWith('image/')) {
            continue;
          }

          const result = await compress(file, options);
          results.push(result);

          // Update overall progress
          setProgress(Math.round(((i + 1) / total) * 100));
        }

        return results;
      } catch (err) {
        const compressionError =
          err instanceof Error ? err : new Error('Batch compression failed');
        setError(compressionError);
        throw compressionError;
      } finally {
        setIsCompressing(false);
      }
    },
    [compress],
  );

  return {
    compress,
    compressBatch,
    isCompressing,
    progress,
    error,
  };
}

/**
 * Check if a file can be compressed (is an image)
 */
export function canCompress(file: File): boolean {
  return file.type.startsWith('image/') && !file.type.includes('svg');
}

/**
 * Get recommended compression options based on use case
 */
export function getCompressionPreset(
  preset: 'thumbnail' | 'preview' | 'standard' | 'high-quality',
): CompressionOptions {
  switch (preset) {
    case 'thumbnail':
      return {
        maxWidthOrHeight: 200,
        maxSizeMB: 0.1,
        quality: 0.7,
        fileType: 'image/webp',
      };
    case 'preview':
      return {
        maxWidthOrHeight: 800,
        maxSizeMB: 0.5,
        quality: 0.75,
        fileType: 'image/webp',
      };
    case 'standard':
      return {
        maxWidthOrHeight: 1920,
        maxSizeMB: 1,
        quality: 0.8,
      };
    case 'high-quality':
      return {
        maxWidthOrHeight: 4096,
        maxSizeMB: 5,
        quality: 0.9,
        preserveExif: true,
      };
    default:
      return DEFAULT_OPTIONS;
  }
}
